import type { AppointmentFormValues } from '@/types/appointment-form';
import type { OrgLocation } from '@/auth/auth-api';
import type { PatientDatum } from '@/types/patient-api';

/**
 * Build the `CreateAppointmentPanel` prefill for the patient-list kebab
 * "Create Appointment" action. Mirrors Flutter
 * `patient_list_screen.dart:788-843` — the menu builds an `Appointment`
 * seeded with ONLY the patient participant (provider left empty) +
 * location + date, then opens the same create flow used on the calendar.
 *
 * Unlike the FU-request prefill (`build-follow-up-from-row.ts`), this sets
 * NO provider and NO `referenceAppointmentId` — it's a fresh appointment,
 * so the profile picker shows all profile types and the user picks the
 * provider / profile / slot in the panel. The PreBook chain
 * (flags / insurance / prices) hydrates the same way it does for the FU
 * prefill once the user selects a provider + profile.
 *
 * `firstLocation` must already be GUID-guarded by the caller (pass null if
 * `organizationLocations[0]` has no real id) — an empty-GUID location is
 * truthy and makes LocationSelector skip its auto-pick, landing on a
 * non-pickable location that 400s on Save. Same guard as
 * `build-follow-up-from-row.ts`.
 */
export function buildCreateAppointmentPrefill(
  patient: PatientDatum,
  firstLocation: OrgLocation | null,
  selectedDateISO: string,
): Partial<AppointmentFormValues> {
  // Explicit real-GUID guard (Samer review, repeated): reject null/undefined,
  // non-string, "", whitespace, AND "0" — not just falsy. Org location ids
  // are backend GUIDs; an empty/"0" id is truthy-but-unusable and makes
  // LocationSelector skip its auto-pick, landing on a non-pickable location
  // that 400s on Save.
  const id = firstLocation?.id;
  const locationHasRealId =
    typeof id === 'string' && id.trim().length > 0 && id.trim() !== '0';

  const name =
    patient.name?.trim() ||
    [patient.firstName, patient.middleName, patient.lastName]
      .map((s) => s?.trim() ?? '')
      .filter((s) => s.length > 0)
      .join(' ') ||
    null;

  return {
    location: locationHasRealId
      ? { id: firstLocation!.id ?? '', name: firstLocation!.name ?? '' }
      : null,
    patientId: patient.patientId ?? null,
    patientName: name,
    patientPhoto: patient.image ?? null,
    patientDob: patient.dob ?? patient.birthDate ?? null,
    patientAge: patient.age ?? null,
    patientGender: patient.gender?.name ?? null,
    patientImportanceCode: patient.importance?.code ?? null,
    patientIsMissingData: !!patient.isMissingData,
    // Calendar day anchor (Flutter `selectedDate: selectedDate`). The user
    // still picks the actual slot/day in the panel.
    selectedDate: selectedDateISO,
  };
}
